import * as React from 'react';
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import { Button } from './button';
import { Skeleton } from './skeleton';
import { cn } from '../../lib/utils';
import { useDesignSystemLabels } from './labels';

export interface StateProps {
  className?: string;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  /**
   * `default` — centred block for a whole empty page or dialog.
   * `inline` — one compact, start-aligned row for a section, table or panel
   * inside a dense layout; no dashed box, no icon tile.
   */
  size?: 'default' | 'inline';
}

function EmptyState({
  className,
  title = 'Nothing here yet',
  description,
  icon,
  action,
  size = 'default',
}: StateProps): React.JSX.Element {
  if (size === 'inline') {
    return (
      <div
        role="status"
        data-cguard-state="empty"
        className={cn(
          'flex flex-wrap items-start gap-x-3 gap-y-2 border-y border-[var(--color-hairline)] px-3 py-3 text-sm',
          className,
        )}
      >
        <span aria-hidden className="mt-0.5 text-[var(--color-foreground-3)] [&_svg]:size-4">
          {icon ?? <Inbox />}
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-medium text-[var(--color-foreground)]">{title}</p>
          {description && <p className="text-xs text-[var(--color-foreground-3)]">{description}</p>}
        </div>
        {action}
      </div>
    );
  }
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card/50 p-10 text-center',
        className,
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon ?? <Inbox className="h-6 w-6" />}
      </div>
      <div className="space-y-1">
        <p className="text-base font-medium text-foreground">{title}</p>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps extends StateProps {
  /** Optional retry handler — wires a default Try-again button when provided. */
  onRetry?: () => void;
  retryLabel?: string;
}

function ErrorState({
  className,
  title: titleProp,
  description,
  onRetry,
  retryLabel,
  action,
  size = 'default',
}: ErrorStateProps): React.JSX.Element {
  const labels = useDesignSystemLabels();
  const title = titleProp ?? labels.error;
  const fallbackAction = onRetry ? (
    <Button onClick={onRetry} variant="outline" size="sm">
      {retryLabel ?? labels.retry}
    </Button>
  ) : null;
  if (size === 'inline') {
    return (
      <div
        role="alert"
        data-cguard-state="error"
        className={cn(
          'flex flex-wrap items-start gap-x-3 gap-y-2 rounded-sm border border-[color-mix(in_oklab,var(--color-danger)_40%,transparent)] bg-[var(--color-danger-tint)] px-3 py-2.5 text-sm',
          className,
        )}
      >
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--color-danger)]" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-medium text-[var(--color-foreground)]">{title}</p>
          {description && <p className="text-xs text-[var(--color-foreground-2)]">{description}</p>}
        </div>
        {action ?? fallbackAction}
      </div>
    );
  }
  return (
    <div
      role="alert"
      data-cguard-state="error"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-10 text-center',
        className,
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <div className="space-y-1">
        <p className="text-base font-medium text-foreground">{title}</p>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action ?? fallbackAction}
    </div>
  );
}

export interface LoadingStateProps {
  className?: string;
  /** "spinner" for inline / dialog loading; "skeleton" for content panes. */
  variant?: 'spinner' | 'skeleton';
  label?: string;
  rows?: number;
  /** `inline` renders table-row-height skeletons for dense layouts. */
  size?: 'default' | 'inline';
}

function LoadingState({
  className,
  variant = 'spinner',
  label: labelProp,
  rows = 4,
  size = 'default',
}: LoadingStateProps): React.JSX.Element {
  const { loading } = useDesignSystemLabels();
  const label = labelProp ?? loading;
  if (variant === 'skeleton') {
    return (
      <div
        role="status"
        aria-label={label}
        data-cguard-state="loading"
        className={cn(size === 'inline' ? 'space-y-1.5' : 'space-y-2', className)}
      >
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className={cn('w-full', size === 'inline' ? 'h-7' : 'h-12')} />
        ))}
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-label={label}
      data-cguard-state="loading"
      className={cn('flex items-center justify-center gap-2 p-6 text-muted-foreground', className)}
    >
      <Loader2 className="h-4 w-4 animate-spin" /> <span className="text-sm">{label}</span>
    </div>
  );
}

export { EmptyState, ErrorState, LoadingState };
